/**
 * Các trang của web admin.
 * type "collapse" hiện trên thanh bên; route không có type chỉ dùng để điều hướng (ví dụ trang chi tiết).
 * type "title" là tiêu đề nhóm: bấm để thu gọn / mở các mục phía sau tới tiêu đề kế tiếp.
 * Nhóm xếp theo tần suất dùng: việc hằng ngày ở trên, phần kỹ thuật (collapsedByDefault) ở dưới.
 * key trùng với đoạn đầu của đường dẫn để thanh bên tô sáng đúng mục.
 * permission: quyền cần để người được phân quyền vào trang, bỏ trống thì chỉ chủ vào được (xem auth/access.js).
 */

import Icon from "@mui/material/Icon";

import { PERMISSIONS } from "auth/access";
import Dashboard from "layouts/dashboard";
import Players from "layouts/players";
import PlayerDetail from "layouts/players/PlayerDetail";
import Mail from "layouts/mail";
import Boss from "layouts/boss";
import Broadcast from "layouts/broadcast";
import ChatModeration from "layouts/chat";
import Balance from "layouts/balance";
import Rates from "layouts/rates";
import GiftCodes from "layouts/giftcodes";
import History from "layouts/history";
import Templates from "layouts/templates";
import Tasks from "layouts/tasks";
import Simulator from "layouts/simulator";
import Permissions from "layouts/permissions";
import ServerSettings from "layouts/server-settings";
import Clans from "layouts/clans";
import Market from "layouts/market";
import Top from "layouts/top";
import Shop from "layouts/shop";
import MythicArena from "layouts/mythic-arena";
import Events from "layouts/events";
import EventSeasons from "layouts/event-seasons";
import ArenaEventPage from "layouts/arena-event";
import Tower from "layouts/tower";
import WorldBoss from "layouts/world-boss";
import Payment from "layouts/payment";
import LuckyWheel from "layouts/lucky-wheel";
import CheckIn from "layouts/checkin";
import Collection from "layouts/collection";
import PetEvolve from "layouts/pet-evolve";
import AutoFarm from "layouts/auto";
import ServerErrors from "layouts/errors";

const routes = [
  {
    type: "collapse",
    name: "Tổng quan",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    permission: PERMISSIONS.SERVER_VIEW,
  },
  { type: "title", title: "Việc hằng ngày", key: "group-daily" },
  {
    type: "collapse",
    name: "Người chơi",
    key: "players",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/players",
    component: <Players />,
    permission: PERMISSIONS.PLAYER_VIEW,
  },
  {
    type: "collapse",
    name: "Thư & quà",
    key: "mail",
    icon: <Icon fontSize="small">mail</Icon>,
    route: "/mail",
    component: <Mail />,
    permission: PERMISSIONS.MAIL_VIEW,
  },
  {
    type: "collapse",
    name: "Nạp tiền",
    key: "payment",
    icon: <Icon fontSize="small">account_balance</Icon>,
    route: "/payment",
    component: <Payment />,
    permission: PERMISSIONS.PAYMENT_VIEW,
  },
  {
    type: "collapse",
    name: "Kiểm duyệt chat",
    key: "chat",
    icon: <Icon fontSize="small">speaker_notes_off</Icon>,
    route: "/chat",
    component: <ChatModeration />,
    permission: PERMISSIONS.CHAT_VIEW,
  },
  {
    type: "collapse",
    name: "Thông báo",
    key: "broadcast",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/broadcast",
    component: <Broadcast />,
    permission: PERMISSIONS.BROADCAST_VIEW,
  },
  {
    type: "collapse",
    name: "Gift code",
    key: "giftcodes",
    icon: <Icon fontSize="small">card_giftcard</Icon>,
    route: "/giftcodes",
    component: <GiftCodes />,
    permission: PERMISSIONS.GIFTCODE_VIEW,
  },
  { type: "title", title: "Sự kiện", key: "group-events" },
  {
    type: "collapse",
    name: "Sự kiện",
    key: "events",
    icon: <Icon fontSize="small">celebration</Icon>,
    route: "/events",
    component: <Events />,
    permission: PERMISSIONS.EVENT_VIEW,
  },
  {
    type: "collapse",
    name: "Mùa sự kiện",
    key: "event-seasons",
    icon: <Icon fontSize="small">calendar_month</Icon>,
    route: "/event-seasons",
    component: <EventSeasons />,
    permission: PERMISSIONS.EVENT_VIEW,
  },
  {
    type: "collapse",
    name: "Vòng quay may mắn",
    key: "lucky-wheel",
    icon: <Icon fontSize="small">casino</Icon>,
    route: "/lucky-wheel",
    component: <LuckyWheel />,
    permission: PERMISSIONS.LUCKY_WHEEL_VIEW,
  },
  {
    type: "collapse",
    name: "Điểm danh tháng",
    key: "checkin",
    icon: <Icon fontSize="small">event_available</Icon>,
    route: "/checkin",
    component: <CheckIn />,
    permission: PERMISSIONS.CHECKIN_VIEW,
  },
  {
    type: "collapse",
    name: "Bộ sưu tập",
    key: "collection",
    icon: <Icon fontSize="small">collections_bookmark</Icon>,
    route: "/collection",
    component: <Collection />,
    permission: PERMISSIONS.COLLECTION_VIEW,
  },
  {
    type: "collapse",
    name: "Lôi đài báo danh",
    key: "arena-event",
    icon: <Icon fontSize="small">sports_kabaddi</Icon>,
    route: "/arena-event",
    component: <ArenaEventPage />,
    permission: PERMISSIONS.ARENA_EVENT_VIEW,
  },
  { type: "title", title: "Nội dung game", key: "group-game" },
  {
    type: "collapse",
    name: "Boss",
    key: "boss",
    icon: <Icon fontSize="small">whatshot</Icon>,
    route: "/boss",
    component: <Boss />,
    permission: PERMISSIONS.BOSS_VIEW,
  },
  {
    type: "collapse",
    name: "Boss thế giới",
    key: "world-boss",
    icon: <Icon fontSize="small">public</Icon>,
    route: "/world-boss",
    component: <WorldBoss />,
    permission: PERMISSIONS.WORLD_BOSS_VIEW,
  },
  {
    type: "collapse",
    name: "Đấu trường thần thú",
    key: "mythic-arena",
    icon: <Icon fontSize="small">emoji_events</Icon>,
    route: "/mythic-arena",
    component: <MythicArena />,
    permission: PERMISSIONS.ARENA_VIEW,
  },
  {
    type: "collapse",
    name: "Tháp thử thách",
    key: "tower",
    icon: <Icon fontSize="small">castle</Icon>,
    route: "/tower",
    component: <Tower />,
    permission: PERMISSIONS.TOWER_VIEW,
  },
  {
    type: "collapse",
    name: "Nhiệm vụ",
    key: "tasks",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/tasks",
    component: <Tasks />,
    permission: PERMISSIONS.TASK_VIEW,
  },
  {
    type: "collapse",
    name: "Bang hội",
    key: "clans",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/clans",
    component: <Clans />,
    permission: PERMISSIONS.CLAN_VIEW,
  },
  {
    type: "collapse",
    name: "Ki ốt",
    key: "market",
    icon: <Icon fontSize="small">storefront</Icon>,
    route: "/market",
    component: <Market />,
    permission: PERMISSIONS.MARKET_VIEW,
  },
  {
    type: "collapse",
    name: "Cửa hàng & nạp",
    key: "shop",
    icon: <Icon fontSize="small">shopping_cart</Icon>,
    route: "/shop",
    component: <Shop />,
    permission: PERMISSIONS.SHOP_VIEW,
  },
  {
    type: "collapse",
    name: "Auto đánh quái",
    key: "auto",
    icon: <Icon fontSize="small">smart_toy</Icon>,
    route: "/auto",
    component: <AutoFarm />,
    permission: PERMISSIONS.AUTO_VIEW,
  },
  {
    type: "collapse",
    name: "Xếp hạng",
    key: "top",
    icon: <Icon fontSize="small">leaderboard</Icon>,
    route: "/top",
    component: <Top />,
    permission: PERMISSIONS.TOP_VIEW,
  },
  { type: "title", title: "Cân bằng & dữ liệu", key: "group-data", collapsedByDefault: true },
  {
    type: "collapse",
    name: "Cân bằng",
    key: "balance",
    icon: <Icon fontSize="small">tune</Icon>,
    route: "/balance",
    component: <Balance />,
    permission: PERMISSIONS.BALANCE_VIEW,
  },
  {
    type: "collapse",
    name: "Tỉ lệ dữ liệu",
    key: "rates",
    icon: <Icon fontSize="small">percent</Icon>,
    route: "/rates",
    component: <Rates />,
    permission: PERMISSIONS.RATE_VIEW,
  },
  {
    type: "collapse",
    name: "Giả lập chỉ số",
    key: "simulator",
    icon: <Icon fontSize="small">science</Icon>,
    route: "/simulator",
    component: <Simulator />,
    permission: PERMISSIONS.SIMULATE_VIEW,
  },
  {
    type: "collapse",
    name: "Tiến hóa pet",
    key: "pet-evolve",
    icon: <Icon fontSize="small">auto_awesome</Icon>,
    route: "/pet-evolve",
    component: <PetEvolve />,
    permission: PERMISSIONS.PET_EVOLVE_VIEW,
  },
  {
    type: "collapse",
    name: "Sửa vật phẩm & chỉ số",
    key: "templates",
    icon: <Icon fontSize="small">inventory_2</Icon>,
    route: "/templates",
    component: <Templates />,
    permission: PERMISSIONS.TEMPLATE_VIEW,
  },
  { type: "title", title: "Hệ thống", key: "group-system", collapsedByDefault: true },
  {
    type: "collapse",
    name: "Lịch sử",
    key: "history",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/history",
    component: <History />,
    permission: PERMISSIONS.HISTORY_VIEW,
  },
  {
    type: "collapse",
    name: "Lỗi máy chủ",
    key: "errors",
    icon: <Icon fontSize="small">bug_report</Icon>,
    route: "/errors",
    component: <ServerErrors />,
    permission: PERMISSIONS.ERROR_VIEW,
  },
  {
    type: "collapse",
    name: "Cấu hình máy chủ",
    key: "server-settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/server-settings",
    component: <ServerSettings />,
    permission: PERMISSIONS.SERVER_VIEW,
  },
  {
    type: "collapse",
    name: "Phân quyền",
    key: "permissions",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/permissions",
    component: <Permissions />,
  },
  // Trang chi tiết không hiện trên thanh bên, chỉ để điều hướng.
  {
    name: "Chi tiết người chơi",
    key: "players-detail",
    route: "/players/:userId",
    component: <PlayerDetail />,
    permission: PERMISSIONS.PLAYER_VIEW,
  },
];

export default routes;
